/* ═══════════════════════════════════════════════════════════════════════
   React Query hooks — data fetching with automatic caching & dedup
   ═══════════════════════════════════════════════════════════════════════ */

import { useQuery, useQueries } from "@tanstack/react-query";
import {
  fetchCompanies,
  fetchSummary,
  fetchTrend,
  fetchCompare,
  searchTickers,
  fetchQuote,
  fetchConceptCatalog,
  fetchPeers,
  fetchPriceHistory,
  fetchFilings,
  fetchInsiders,
  fetchInsiderTrades,
  fetchInstitutionalHolders,
  fetchFundHoldings,
  fetchOperationsSummary,
  fetchOperationsGrid,
  fetchOperationsEnvironmental,
  fetchMacroGrid,
  fetchGridSlippage,
  fetchBitemporalHistory,
  fetchBessRteSeries,
  fetchQueueVelocitySeries,
  fetchRtoFuelMixSeries,
} from "../api/client";

// ── Companies (fetched once, cached forever) ─────────────────────────

export function useCompanies() {
  return useQuery({
    queryKey: ["companies"],
    queryFn: fetchCompanies,
    staleTime: Infinity,
  });
}

// ── Insiders & Holders ───────────────────────────────────────────────

export function useInsiders(ticker: string | undefined) {
  return useQuery({
    queryKey: ["insiders", ticker],
    queryFn: () => fetchInsiders(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInsiderTrades(ticker: string | undefined) {
  return useQuery({
    queryKey: ["insiderTrades", ticker],
    queryFn: () => fetchInsiderTrades(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInstitutionalHolders(ticker: string | undefined) {
  return useQuery({
    queryKey: ["institutionalHolders", ticker],
    queryFn: () => fetchInstitutionalHolders(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFilings(ticker: string | undefined) {
  return useQuery({
    queryKey: ["filings", ticker],
    queryFn: () => fetchFilings(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

export function useFundHoldings(ticker: string | undefined, top = 50) {
  return useQuery({
    queryKey: ["fundHoldings", ticker, top],
    queryFn: () => fetchFundHoldings(ticker!, top),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

// ── Summary per ticker ───────────────────────────────────────────────

export function useSummary(exchange: string | undefined, ticker: string | undefined) {
  return useQuery({
    queryKey: ["summary", exchange, ticker],
    queryFn: () => fetchSummary(exchange!, ticker!),
    enabled: !!ticker && !!exchange,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

// ── Trend (single) ──────────────────────────────────────────────────

export function useTrend(
  exchange: string | undefined,
  ticker: string | undefined,
  concept: string,
  periods = 8,
  form?: string,
) {
  return useQuery({
    queryKey: ["trend", exchange, ticker, concept, periods, form],
    queryFn: () => fetchTrend(exchange!, ticker!, concept, periods, form),
    enabled: !!ticker && !!exchange,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Trend (batch — for charts builder with multiple entity×metric) ──

export function useTrendBatch(
  queries: Array<{
    exchange: string;
    ticker: string;
    concept: string;
    periods: number;
    form?: string;
  }>,
) {
  return useQueries({
    queries: queries.map((q) => ({
      queryKey: ["trend", q.exchange, q.ticker, q.concept, q.periods, q.form],
      queryFn: () => fetchTrend(q.exchange, q.ticker, q.concept, q.periods, q.form),
      staleTime: 5 * 60 * 1000,
    })),
  });
}

// ── Compare ──────────────────────────────────────────────────────────

export function useCompare(
  exchange: string | undefined,
  concept: string,
  tickers: string[],
  periods = 1,
  form?: string,
) {
  return useQuery({
    queryKey: ["compare", exchange, concept, tickers, periods, form],
    queryFn: () => fetchCompare(exchange!, concept, tickers, periods, form),
    enabled: tickers.length > 0 && !!exchange,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Ticker Universe search (debounced) ───────────────────────────────

export function useTickerSearch(query: string) {
  return useQuery({
    queryKey: ["tickerSearch", query],
    queryFn: () => searchTickers(query, 10),
    enabled: query.trim().length >= 1,
    staleTime: 30 * 1000, // 30s
    placeholderData: (prev) => prev,
  });
}

// ── Quote ────────────────────────────────────────────────────────────

export function useQuote(ticker: string | undefined) {
  return useQuery({
    queryKey: ["quote", ticker],
    queryFn: () => fetchQuote(ticker!),
    enabled: !!ticker,
    staleTime: 60 * 1000, // 1 min
    retry: 1,
  });
}

// ── Concept Catalog (per company — for dynamic metric discovery) ─────

export function useConceptCatalog(exchange: string | undefined, ticker: string | undefined) {
  return useQuery({
    queryKey: ["conceptCatalog", exchange, ticker],
    queryFn: () => fetchConceptCatalog(exchange!, ticker!),
    enabled: !!ticker && !!exchange,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Peers (SIC-matched) ──────────────────────────────────────────────

export function usePeers(ticker: string | undefined) {
  return useQuery({
    queryKey: ["peers", ticker],
    queryFn: () => fetchPeers(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Price History ────────────────────────────────────────────────────

export function usePriceHistory(ticker: string | undefined, period = "1y") {
  return useQuery({
    queryKey: ["priceHistory", ticker, period],
    queryFn: () => fetchPriceHistory(ticker!, period),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

// ── Operations & Energy Grid ─────────────────────────────────────────

export function useOperationsSummary(ticker: string | undefined, asOfSystemTime?: string) {
  return useQuery({
    queryKey: ["operationsSummary", ticker, asOfSystemTime],
    queryFn: () => fetchOperationsSummary(ticker!, asOfSystemTime),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useOperationsGrid(ticker: string | undefined, isoRto?: string, status?: string, asOfSystemTime?: string) {
  return useQuery({
    queryKey: ["operationsGrid", ticker, isoRto, status, asOfSystemTime],
    queryFn: () => fetchOperationsGrid(ticker!, isoRto, status, asOfSystemTime),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useOperationsEnvironmental(ticker: string | undefined, statute?: string, violationsOnly = false) {
  return useQuery({
    queryKey: ["operationsEnvironmental", ticker, statute, violationsOnly],
    queryFn: () => fetchOperationsEnvironmental(ticker!, statute, violationsOnly),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMacroGrid(isoRto?: string, resourceType?: string, minCapacityMw?: number) {
  return useQuery({
    queryKey: ["macroGrid", isoRto, resourceType, minCapacityMw],
    queryFn: () => fetchMacroGrid(isoRto, resourceType, minCapacityMw),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGridSlippage(minSlippageMonths = 3, isoRto?: string, limit = 50) {
  return useQuery({
    queryKey: ["gridSlippage", minSlippageMonths, isoRto, limit],
    queryFn: () => fetchGridSlippage(minSlippageMonths, isoRto, limit),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBitemporalHistory(systemTime: string | undefined, entityType?: string, entityId?: string, isoRegion?: string) {
  return useQuery({
    queryKey: ["bitemporalHistory", systemTime, entityType, entityId, isoRegion],
    queryFn: () => fetchBitemporalHistory(systemTime!, entityType, entityId, isoRegion),
    enabled: !!systemTime,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBessRteSeries(facilityId: number | string | undefined) {
  return useQuery({
    queryKey: ["bessRteSeries", facilityId],
    queryFn: () => fetchBessRteSeries(facilityId!),
    enabled: !!facilityId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useQueueVelocitySeries(
  iso: string | undefined,
  frequency = "quarterly",
  startYear = 2000,
  endYear = 2025,
  resourceType?: string,
) {
  return useQuery({
    queryKey: ["queueVelocitySeries", iso, frequency, startYear, endYear, resourceType],
    queryFn: () => fetchQueueVelocitySeries(iso!, frequency, startYear, endYear, resourceType),
    enabled: !!iso,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRtoFuelMixSeries(
  ba: string | undefined,
  startDate?: string,
  endDate?: string,
  limit = 365,
) {
  return useQuery({
    queryKey: ["rtoFuelMixSeries", ba, startDate, endDate, limit],
    queryFn: () => fetchRtoFuelMixSeries(ba!, startDate, endDate, limit),
    enabled: !!ba,
    staleTime: 5 * 60 * 1000,
  });
}



/* ── Model Signals (gated) ─────────────────────────────────────────── */
import {
  fetchModelAccess,
  fetchModels,
  fetchModelPredictions,
  fetchModelPerformance,
} from "../api/client";

export function useModelAccess() {
  return useQuery({
    queryKey: ["modelAccess"],
    queryFn: fetchModelAccess,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useModels(enabled = true) {
  return useQuery({
    queryKey: ["models"],
    queryFn: fetchModels,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useModelPredictions(
  modelName: string | undefined,
  decision?: string,
  outcome?: string,
) {
  return useQuery({
    queryKey: ["modelPredictions", modelName, decision ?? "all", outcome ?? "any"],
    queryFn: () => fetchModelPredictions(modelName as string, decision, outcome),
    enabled: !!modelName,
    staleTime: 5 * 60 * 1000,
  });
}

export function useModelPerformance(modelName: string | undefined) {
  return useQuery({
    queryKey: ["modelPerformance", modelName],
    queryFn: () => fetchModelPerformance(modelName as string),
    enabled: !!modelName,
    staleTime: 5 * 60 * 1000,
  });
}
